import React, { useEffect, useState } from "react";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import {
    CompetitionOverview as Overview,
    // DiscussionList,
    TeamView,
    Terms,
    CompetitionScoreboard,
    CompetitionViewHandler,
    Announcements,
    CLogin,
    CRelogin,
    CUnauthorized,
    CErrorDisplayer,
    OwnerOverview,
    OwnerTerms,
    OwnerAnnouncements,
    CompSettings,
    OwnerTeams,
    Checklist,
    CollaboratorTeam,
    CNotFound,
} from "@components/competition";
import { Header } from "@components";
import { getCompetition } from "@util/api/competitions";
import { COMPETITION_TABS, OWNER_COMPETITION_TABS } from "@util/constants";
import Head from "next/head";
import { useSelector } from "react-redux";
import { ICompData } from "@util/interfaces";
import { XCircle, Frown, XSquare, X } from "react-feather";
import { Heading, Text, useToast } from "@chakra-ui/react";
import ErrorInternalServerError from "pages/_error";
import { getDateStatus } from "@util/dateConverter";
import { IUser } from "@redux/slices/user";

const CompetitionViewPage: NextPage = (props: {
    data: ICompData;
    errorCode?: number;
    children: JSX.Element;
    page_type: "unauthorized" | "login" | "data" | "relogin" | "internal_error" | "not_found" | "not_participant";
}) => {
    const toast = useToast();
    const router = useRouter();
    const user = useSelector((state: { user: IUser }) => state.user);

    const [editConfirmed, setEditConfirmed] = useState(false);
    const [compData, setCompData] = useState(props.data);
    const [hasError, setHasError] = useState(false);
    const [errorDetails, setErrorDetails] = useState({ code: 0, text: "" });
    const [hasChanged, setHasChanged] = useState(false);

    useEffect(() => {
        if (editConfirmed) resetCompData();
    }, [editConfirmed]);

    const resetCompData = async () => {
        const serverResponse = (await getCompetition(router.query.competition, {
            toast,
            requesterPath: router.asPath,
        })) as Response;
        if (serverResponse.ok) {
            const compData = await serverResponse.json();
            switch (compData.participation_type) {
                case 10:
                default:
                    compData.participation_type = "انفرادی";
                    break;
                case 30:
                    compData.participation_type = "انفرادی و تیمی";
                    break;
            }

            compData.date_published = getDateStatus(compData.date_published);
            setCompData(compData);
        } else {
            setHasError(true);
            setErrorDetails({
                code: serverResponse.status,
                text: JSON.stringify(serverResponse.json()),
            });
        }

        setEditConfirmed(false);
        setHasChanged(false);
    };
    if (props.errorCode) {
        return <ErrorInternalServerError statusCode={props.errorCode} />;
    } else {
        const isOwner = props.data?.owner.fusion_user_id === user.data.id;
        const isCollaborator = props.data?.collaborators.some((value) => value.user.fusion_user_id === user.data.id);
        const collaboratorDetails = props.data?.collaborators.find((value) => value.user.username === user.data.username);
        const type = props.data?.is_classroom ? "کلاس درس" : "مسابقه";

        let currentViewObj: { label: string; key: string; icon: JSX.Element } = { icon: null, label: "", key: "" };
        if (isOwner) {
            Object.values(OWNER_COMPETITION_TABS).forEach((el) => {
                if (el.key === router.query.comp_view) {
                    currentViewObj = el;
                    return;
                }
            });
        } else {
            Object.values(COMPETITION_TABS).forEach((el) => {
                if (el.key === router.query.comp_view) {
                    currentViewObj = el;
                    return;
                }
            });
        }
        let currentViewComponent: React.ReactElement = null;
        let currentViewTitle = "";
        if (props.page_type === "data") {
            switch (router.query.comp_view) {
                // case "discussion":
                //     currentViewComponent = <DiscussionList compId={compData.id} />;
                //     currentViewTitle = "گفت و گو";
                //     break;
                case "overview":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerOverview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                                toast={toast}
                                router={router}
                            />
                        ) : (
                            <Overview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                            />
                        );
                    break;

                case "organizers":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <CollaboratorTeam
                                isOwner={isOwner}
                                isCollaborator={isCollaborator}
                                owner={props.data.owner}
                                toast={toast}
                                router={router}
                            />
                        ) : (
                            <CErrorDisplayer hasButton />
                        );
                    break;
                case "team":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerTeams
                                isOwner={isOwner}
                                isCollaborator={isCollaborator}
                                owner={props.data.owner}
                                toast={toast}
                                router={router}
                            />
                        ) : compData.participant_id && compData.participation_type !== "انفرادی" ? (
                            <TeamView compData={compData} toast={toast} router={router} user={user} />
                        ) : (
                            <CErrorDisplayer
                                icon={compData.participation_type !== "انفرادی" ? XCircle : Frown}
                                text={
                                    compData.participation_type !== "انفرادی" ? (
                                        <Text>
                                            برای عضویت در یک تیم در این {type} ابتدا باید در {type} شرکت داشته باشید. <br /> اگر
                                            در این {type} شرکت کرده‌اید و این صفحه برایتان به نمایش درآمده وارد حساب کاربری خود
                                            در سامانه روبواپیکس شوید. <br /> در صورت انجام دادن مراحل بالا و مواجهه دوباره با
                                            این صفحه با پشتیبانی روبواپیکس تماس بگیرید.
                                        </Text>
                                    ) : (
                                        <Text>
                                            این {type} به صورت انفرادی برگزار می‌شود و امکان عضویت در تیم برای شرکت‌کنندگان وجود
                                            ندارد
                                        </Text>
                                    )
                                }
                                hasButton
                            />
                        );
                    currentViewTitle = "تیم";
                    break;
                case "rules":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerTerms rules={compData.rules} router={router} toast={toast} />
                        ) : (
                            <Terms rules={compData.rules} />
                        );
                    currentViewTitle = "آیین‌نامه";
                    break;
                case "scoreboard":
                    currentViewComponent = <CompetitionScoreboard competition={compData.path} toast={toast} router={router} />;
                    currentViewTitle = "امتیازها";
                    break;
                case "announcements":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerAnnouncements competition={compData.path} toast={toast} router={router} />
                        ) : (
                            <Announcements competition={compData.path} toast={toast} router={router} />
                        );
                    currentViewTitle = "اعلان‌ها";
                    break;
                case "settings":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            hasError ? (
                                <CErrorDisplayer
                                    text={
                                        <>
                                            <Heading size="lg">{errorDetails.code}</Heading>
                                            <Text>{errorDetails.text}</Text>
                                            <Text>
                                                <kbd>Inspect</kbd> for error Object logged in "console" tab
                                            </Text>
                                        </>
                                    }
                                    hasButton
                                />
                            ) : (
                                <CompSettings
                                    settingsData={{
                                        access_level: compData.access_level,
                                        participation_type:
                                            compData.participation_type === "انفرادی"
                                                ? 10
                                                : compData.participation_type === "تیمی"
                                                ? 20
                                                : 30,
                                        team_member_limit: compData.team_member_limit,
                                        participant_limit: compData.participant_limit,
                                        daily_submission_limit: compData.daily_submission_limit,
                                        register_date_start: compData.register_date_start,
                                        register_date_end: compData.register_date_end,
                                        subtitle: compData.subtitle,
                                        title: compData.title,
                                        prize: compData.prize,
                                        is_classroom: compData.is_classroom,
                                        path: compData.path,
                                        discussion_url: compData.discussion_url,
                                    }}
                                    thumbnail={compData.thumbnail}
                                    competition={compData.path}
                                    setEditConfirmed={setEditConfirmed}
                                    competitionStatus={props.data.status}
                                    router={router}
                                    toast={toast}
                                />
                            )
                        ) : (
                            <CErrorDisplayer hasButton />
                        );
                    currentViewTitle = "تنظیمات";
                    break;
                case "checklist":
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <Checklist
                                isPublished={compData.is_published}
                                isInvalid={compData.is_invalid}
                                hasOverview={compData.description ? compData.description.length !== 0 : false}
                                hasCoverImage={!!compData.cover_image}
                                hasRegistrationStartDate={!!compData.register_date_start}
                                hasPhase={compData.phase_set.length !== 0}
                                hasProblem={compData.phase_set.some((phase) => phase.problems.length !== 0)}
                                router={router}
                                toast={toast}
                            />
                        ) : (
                            <CErrorDisplayer hasButton />
                        );
                    currentViewTitle = "چک‌لیست";
                    break;
                case undefined:
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerOverview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                                toast={toast}
                                router={router}
                            />
                        ) : (
                            <Overview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                            />
                        );
                    break;
                default:
                    currentViewComponent =
                        isOwner || isCollaborator ? (
                            <OwnerOverview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                                toast={toast}
                                router={router}
                            />
                        ) : (
                            <Overview
                                description={compData.description}
                                contentType={compData.content_type}
                                coverImage={compData.cover_image}
                            />
                        );
                    break;
            }
        }
        const getPageContent = (
            page_type: "unauthorized" | "login" | "data" | "relogin" | "internal_error" | "not_found" | "not_participant",
        ) => {
            switch (page_type) {
                case "login":
                    return <CLogin />;
                case "data":
                    return (
                        <CompetitionViewHandler
                            data={compData}
                            currentViewComponent={currentViewComponent}
                            currentViewObj={currentViewObj}
                            isOwner={isOwner}
                            isCollaborator={isCollaborator}
                            collaborationAccessLevel={isCollaborator ? collaboratorDetails.access_level : undefined}
                            setHasChanged={setHasChanged}
                            toast={toast}
                            router={router}
                            user={user}
                        />
                    );
                case "relogin":
                    return <CRelogin />;
                case "unauthorized":
                    return <CUnauthorized />;
                case "internal_error":
                    return (
                        <CErrorDisplayer
                            text={
                                <Text fontSize={"xl"} textAlign="center">
                                    مشکلی در نمایش اطلاعات رخ داده است. <br /> لطفاً دوباره تلاش کنید.
                                </Text>
                            }
                            icon={XSquare}
                            hasButton={false}
                        />
                    );
                case "not_found":
                    return <CNotFound />;
                case "not_participant":
                    return (
                        <CErrorDisplayer
                            text={<Text fontSize={"xl"}>برای دیدن محتوای این صفحه باید در {type} شرکت کرده باشید</Text>}
                            icon={X}
                            hasButton={false}
                        />
                    );
                default:
                    // Custom Loading Component
                    break;
            }
        };

        return (
            <>
                <Head>
                    <title>
                        RoboEpics | {props.page_type === "data" ? `${compData.title} - ${currentViewTitle}` : props.page_type}
                    </title>
                    <meta name="viewport" content="initial-scale=1.0, width=device-width" />
                </Head>
                <Header toast={toast} router={router} user={user} />
                {getPageContent(props.page_type)}
            </>
        );
    }
};

export const getServerSideProps: GetServerSideProps = async ({ params, res, req }) => {
    const serverResponse = await getCompetition(params.competition, { res, req });
    if (serverResponse instanceof Response) {
        if (serverResponse.ok) {
            const compData = await serverResponse.json();
            switch (compData.participation_type) {
                case 10:
                default:
                    compData.participation_type = "انفرادی";
                    break;
                case 30:
                    compData.participation_type = "انفرادی و تیمی";
                    break;
            }

            compData.date_published = getDateStatus(compData.date_published);
            return { props: { data: compData, page_type: "data" } };
        } else {
            switch (serverResponse.status) {
                case 404:
                    return { props: { page_type: "not_found" } };
                case 403:
                    return { props: { page_type: "not_participant" } };

                case 400:
                    console.log(await serverResponse.json());
                    return { props: { page_type: "internal_error" } };
                case 500:
                default:
                    return { props: { page_type: "internal_error" } };
            }
        }
    } else {
        return {
            redirect: serverResponse,
        };
    }
};

export default CompetitionViewPage;
